import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import songAdService, { SongAdProject } from '../../services/api/songAdService';
import SongAdCreatePanel from '../../components/song-ad/SongAdCreatePanel';
import { Music2, Trash2, Film } from 'lucide-react';
import { ResponsiveAppShell, TaskyBadge, TaskyCard, TaskyEmptyState, TaskySkeleton, TaskyAlert, TaskyBadgeProps, TaskyTopBar, useConfirm, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';

/**
 * 歌広告（Song Ad Video）プロジェクト一覧ページ。
 *
 * /ads/banner 踏襲のレイアウト: ページ上部に常時表示の新規作成パネル（SongAdCreatePanel）、
 * その下に作成済みプロジェクトのグリッド一覧。ダイアログ型の作成モーダルは廃止。
 * 作成後はエディタ（/ads/song/:projectId）へ遷移する。
 */

const STATUS_BADGE: Record<string, { label: string; variant?: TaskyBadgeProps['variant']; className?: string }> = {
  draft: { label: '制作中', variant: 'secondary' },
  lyrics_generating: { label: '歌詞生成中', variant: 'info' },
  lyrics_ready: { label: '歌詞準備完了', variant: 'info' },
  song_generating: { label: '曲生成中', variant: 'info' },
  song_ready: { label: '曲準備完了', className: 'bg-purple/10 text-purple' },
  song_failed: { label: '曲生成失敗', variant: 'destructive' },
  scenes_ready: { label: 'シーン準備完了', className: 'bg-purple/10 text-purple' },
  composing: { label: '合成中', variant: 'info' },
  completed: { label: '完成', variant: 'success' },
  compose_failed: { label: '合成失敗', variant: 'destructive' },
};

const SongAdListPage: React.FC = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState<SongAdProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await songAdService.getProjects();
      setProjects(data);
    } catch (e) {
      setError(songAdService.extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreated = (project: SongAdProject) => {
    showToast('歌広告プロジェクトを作成しました');
    navigate(`/ads/song/${project.id}`);
  };

  const handleDelete = async (e: React.MouseEvent, project: SongAdProject) => {
    e.stopPropagation();
    if (!(await confirm({ title: `「${project.title}」を削除しますか？シーン・生成履歴との紐付けも解除されます。`, tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }
    try {
      await songAdService.deleteProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(songAdService.extractErrorMessage(err));
    }
  };

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle="歌広告"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="歌広告"
          icon={<Music2 className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Main */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            {error && (
              <TaskyAlert tone="error" className="mb-4">
                {error}
              </TaskyAlert>
            )}

            {/* 新規作成パネル（常時表示） */}
            <div data-tutorial="ads-song-new">
              <SongAdCreatePanel onCreated={handleCreated} />
            </div>

            {/* 作成済み一覧 */}
            <div data-tutorial="ads-song-list">
            <h2 className="text-sm font-semibold text-dark mb-3">作成済みプロジェクト</h2>

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-tasky-lg overflow-hidden bg-white border border-warm-border">
                    <TaskySkeleton className="aspect-video w-full rounded-none" />
                    <div className="p-3 space-y-2">
                      <TaskySkeleton shape="text" className="w-3/4" />
                      <TaskySkeleton shape="text" className="w-1/3 h-3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : projects.length === 0 ? (
              <TaskyEmptyState
                icon={<Music2 />}
                title="プロジェクトがありません"
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => {
                  const status = STATUS_BADGE[project.status] ?? STATUS_BADGE.draft;
                  return (
                    <TaskyCard
                      key={project.id}
                      variant="bordered"
                      padding="none"
                      hoverable
                      onClick={() => navigate(`/ads/song/${project.id}`)}
                      className="overflow-hidden"
                    >
                      <div className="aspect-video bg-gray-100 flex items-center justify-center overflow-hidden">
                        {project.thumbnail_url ? (
                          <img
                            src={project.thumbnail_url}
                            alt={project.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Film className="w-10 h-10 text-gray-300" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <h3 className="text-sm font-semibold text-dark truncate">{project.title}</h3>
                          <button
                            onClick={(e) => handleDelete(e, project)}
                            className="p-1 text-gray-400 hover:text-red-500 shrink-0"
                            title="削除"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <TaskyBadge variant={status.variant} className={status.className}>
                            {status.label}
                          </TaskyBadge>
                          {typeof project.scene_count === 'number' && project.scene_count > 0 && (
                            <span>{project.scene_count}シーン</span>
                          )}
                        </div>
                      </div>
                    </TaskyCard>
                  );
                })}
              </div>
            )}
            </div>
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default SongAdListPage;
